import React, { useEffect, useMemo, useState } from 'react';
import { getRiskLog } from '../api';
import { ClassBadge } from './ClassBadge';
import { Inbox, Loader2, FlaskConical } from 'lucide-react';
import { formatTradeTime } from '../utils/time';
import { KpiCard } from './ui/KpiCard';
import { EmptyState } from './ui/EmptyState';
import { TableWrapper, Thead, Th, Tbody, Tr, Td } from './ui/TableWrapper';
import { InstrumentCell } from './ui/InstrumentCell';

const SHADOW_CLASSES = ['BIAS_SNIPE', 'DAILY_TREND'];

const REASON_LABELS: Record<string, string> = {
  bias_snipe_virtual_entry: 'Virtual Entry',
  bias_snipe_virtual_exit: 'Virtual Exit',
  bias_snipe_chase_reject: 'Chase Reject',
  daily_trend_bias_set: 'Bias Set (virtual entry)',
  daily_trend_virtual_exit: 'Virtual Exit',
  daily_trend_bias_invalidated: 'Bias Invalidated',
};

const isEntry = (reason: string) => reason === 'bias_snipe_virtual_entry' || reason === 'daily_trend_bias_set';
const isExit = (reason: string) => reason === 'bias_snipe_virtual_exit' || reason === 'daily_trend_virtual_exit';

const fmtPrice = (v: any) => (typeof v === 'number' ? v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '--');

function classStats(rows: any[], cls: string) {
  const mine = rows.filter(r => r.signal_class === cls);
  const exits = mine.filter(r => isExit(r.details?.reason || ''));
  const entries = mine.filter(r => isEntry(r.details?.reason || ''));
  const pnl = exits.reduce((sum, r) => sum + (Number(r.details?.virtual_pnl_per_contract) || 0), 0);
  const wins = exits.filter(r => (Number(r.details?.virtual_pnl_per_contract) || 0) > 0).length;
  return { entries: entries.length, exits: exits.length, pnl, wins, losses: exits.length - wins };
}

export default function ShadowObservers() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [classFilter, setClassFilter] = useState<string>('all');

  useEffect(() => {
    let first = true;
    const load = async () => {
      if (first) setLoading(true);
      try {
        const results = await Promise.all(
          SHADOW_CLASSES.map(cls => getRiskLog({ event: 'shadow_block', signal_class: cls, limit: 200 })),
        );
        const merged = results.flatMap(r => r.data || [])
          // Defensive re-filter: guards against a backend that ignores the
          // query params (pre-param deploy) returning unfiltered rows.
          .filter((r: any) => r.event === 'shadow_block' && SHADOW_CLASSES.includes(r.signal_class))
          .sort((a: any, b: any) => String(b.created_at).localeCompare(String(a.created_at)));
        setRows(merged);
      } catch (e) { console.error(e); }
      finally { if (first) { setLoading(false); first = false; } }
    };
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const visible = useMemo(
    () => rows.filter(r => classFilter === 'all' || r.signal_class === classFilter),
    [rows, classFilter],
  );

  const bs = useMemo(() => classStats(rows, 'BIAS_SNIPE'), [rows]);
  const dt = useMemo(() => classStats(rows, 'DAILY_TREND'), [rows]);

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 p-3 rounded-lg border border-dashed border-gray-600 bg-gray-900/60">
        <FlaskConical size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-gray-400">
          <span className="font-semibold text-gray-300">Shadow observers — no real trades.</span>{' '}
          BIAS_SNIPE and DAILY_TREND are evaluation-only models. Every row here is a virtual
          entry/exit logged for research; no orders are placed and all P&amp;L is hypothetical,
          per one contract.
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label="BIAS_SNIPE Virtual P&L"
          value={`${bs.pnl > 0 ? '+' : ''}$${bs.pnl.toFixed(2)}`}
          tone={bs.pnl > 0 ? 'positive' : bs.pnl < 0 ? 'negative' : 'neutral'}
          sub={`${bs.wins}W / ${bs.losses}L closed (hypothetical)`}
        />
        <KpiCard
          label="BIAS_SNIPE Entries"
          value={String(bs.entries)}
          tone="neutral"
          sub={`${bs.exits} virtual exits`}
        />
        <KpiCard
          label="DAILY_TREND Virtual P&L"
          value={`${dt.pnl > 0 ? '+' : ''}$${dt.pnl.toFixed(2)}`}
          tone={dt.pnl > 0 ? 'positive' : dt.pnl < 0 ? 'negative' : 'neutral'}
          sub={`${dt.wins}W / ${dt.losses}L closed (hypothetical)`}
        />
        <KpiCard
          label="DAILY_TREND Entries"
          value={String(dt.entries)}
          tone="neutral"
          sub={`${dt.exits} virtual exits`}
        />
      </div>

      <div className="flex items-center justify-end">
        <select value={classFilter} onChange={e => setClassFilter(e.target.value)}
          className="bg-gray-800 text-xs text-gray-300 px-2 py-1 rounded border border-gray-700">
          <option value="all">Both Observers</option>
          <option value="BIAS_SNIPE">BIAS_SNIPE</option>
          <option value="DAILY_TREND">DAILY_TREND</option>
        </select>
      </div>

      <div className="overflow-y-auto max-h-[600px] rounded-lg border border-dashed border-gray-700">
        <TableWrapper>
          <Thead>
            <Th>Time</Th>
            <Th>Inst</Th>
            <Th>Observer</Th>
            <Th>Event</Th>
            <Th>Dir</Th>
            <Th>Entry</Th>
            <Th>Stop</Th>
            <Th>Target</Th>
            <Th>Exit</Th>
            <Th>Virtual P&L / ct</Th>
          </Thead>
          <Tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="px-3 py-6">
                  <div className="flex flex-col items-center justify-center text-gray-500 gap-2 py-6">
                    <Loader2 size={24} className="animate-spin" />
                    <span className="text-sm">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-3 py-6">
                  <EmptyState icon={<Inbox size={28} />} message="No shadow observer events yet" sub="Virtual entries and exits from BIAS_SNIPE and DAILY_TREND will appear here." />
                </td>
              </tr>
            ) : visible.map((r: any) => {
              const d = r.details || {};
              const reason: string = d.reason || '';
              const pnl = d.virtual_pnl_per_contract;
              const hasPnl = typeof pnl === 'number';
              const exitDetail = [
                d.kind ? String(d.kind) : null,
                typeof d.peak_r === 'number' ? `peak ${d.peak_r.toFixed(2)}R` : null,
              ].filter(Boolean).join(' · ');
              return (
                <Tr key={r.id}>
                  <Td className="text-gray-400 text-xs">{formatTradeTime(r.created_at)}</Td>
                  <Td className="font-medium"><InstrumentCell symbol={r.instrument || ''} /></Td>
                  <Td className="whitespace-nowrap"><ClassBadge signalClass={r.signal_class} /></Td>
                  <Td className="text-xs">
                    <span title={reason} className="cursor-help">{REASON_LABELS[reason] || reason || '--'}</span>
                    {exitDetail && <span className="text-gray-500 ml-1">({exitDetail})</span>}
                  </Td>
                  <Td>
                    {d.direction ? (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${d.direction === 'long' ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'}`}>
                        {String(d.direction).toUpperCase()}
                      </span>
                    ) : <span className="text-gray-600">--</span>}
                  </Td>
                  <Td className="font-mono text-xs">{fmtPrice(d.entry_price)}</Td>
                  <Td className="font-mono text-xs">{fmtPrice(d.stop_price ?? d.bias_stop_level)}</Td>
                  <Td className="font-mono text-xs">{fmtPrice(d.target_price)}</Td>
                  <Td className="font-mono text-xs">{fmtPrice(d.exit_price ?? (reason === 'daily_trend_bias_invalidated' ? d.observed_price : undefined))}</Td>
                  <Td className={`font-mono text-xs ${hasPnl ? (pnl >= 0 ? 'text-green-400' : 'text-red-400') : 'text-gray-600'}`}>
                    {hasPnl ? `${pnl > 0 ? '+' : ''}$${pnl.toFixed(2)}` : '--'}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </TableWrapper>
      </div>
      <div className="text-[10px] text-gray-500 px-1">
        Showing the {rows.length} most recent shadow events (up to 200 per observer). Virtual P&amp;L is per contract, before commissions.
      </div>
    </div>
  );
}
